<template>
    <div>
        <!--
            Both editor runtimes ship while teams can still be sent back
            to v3 with the `editor_v3` Kiosk feature. The two urls below go only to v3, which is given a separate
            endpoint for reading one autosave where v4 derives it from the
            listing url - forwarding them explicitly keeps them off v4 and out
            of the DOM. Removing the flag means deleting EditorV3.vue and
            collapsing this back to a single element.
        -->
        <EditorV4
            v-if="editorV4"
            v-bind="shared"
            :nonce-url="nonceUrl"
        ></EditorV4>
        <EditorV3
            v-else
            v-bind="shared"
            :get-autosave-url="getAutosaveUrl"
            :create-autosave-url="createAutosaveUrl"
        ></EditorV3>
    </div>
</template>

<script setup lang="ts">
import EditorV3 from '@components/Editor/EditorV3.vue';
import EditorV4 from '@components/Editor/EditorV4.vue';
import { IStage } from '@topol.io/editor-vue';
import IDefaultTemplateSettings from '@/types/Settings/IDefaultTemplateSettings';

type CurrentUser = {
    id: number;
    name: string;
    profile_photo_path: string;
    profile_photo_url: string;
};

interface Props {
    apiKey: string;
    token: string;
    definition: string;
    saveUrl: string;
    pingUrl: string;
    backUrl: string;
    saveSavedBlockUrl: string;
    savedSectionsUrl: string;
    teamId: number;
    startFromScratch: boolean;
    enableSubjectLine: boolean;
    premadeTemplatesUrl: string;
    premadeTemplateCategoriesUrl: string;
    premadeTemplateKeywordsUrl: string;
    getAutosaveUrl: string;
    getConversationsUrl: string;
    getCommentsUrl: string;
    sendTestEmailUrl: string;
    createAutosaveUrl: string;
    getAutosavesUrl: string;
    theme: string;
    fontSizes: string;
    defaultFonts: string;
    customFonts: string;
    smartMergeTags: string;
    mergeTags: string;
    currentUser: CurrentUser;
    ownerId: string | number;
    templateId: number;
    language?: string;
    colors: string;
    title?: string;
    stage?: IStage;
    enableMergeTagPreview?: boolean;
    imageCompressionOptions: string;
    getFeedsUrl: string;
    getProductsUrl: string;
    enableImageToTextRatio: boolean;
    chatAI: boolean;
    googlePromotionalAnnotation: boolean;
    lambdaStage: string;
    userRole: string;
    templateStatus: string | null;
    htmlMinified: boolean;
    enableSectionVariants: boolean;
    emailServiceProvider?: 'mailchimp'|'sparkpost';
    isSubscribed: boolean;
    testingEmails: string;
    teamUsers: string;
    multilingual: boolean;
    /** What a new template starts as; null keeps the editor's own defaults. */
    defaultTemplateSettings?: IDefaultTemplateSettings | null;
    /** Whether this template's team is opted into the Email Editor v4 runtime. */
    editorV4: boolean;
    /** Set only when the team authenticates the v4 editor with nonces. */
    nonceUrl?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
    language: 'en',
    title: '',
    stage: 'production',
    lambdaStage: 'production',
    nonceUrl: null,
    defaultTemplateSettings: null,
});

// These pick and configure the runtime rather than describing the
// template, so they are split off here instead of being forwarded blindly.
const { editorV4, nonceUrl, getAutosaveUrl, createAutosaveUrl, ...shared } =
    props;
</script>

<script lang="ts">
import BlankLayout from '@/Layouts/Blank.vue';

export default {
    layout: BlankLayout,
};
</script>
